import React, { useEffect, useRef, useState } from "react";
import { useContent } from 'hooks/useContent';

interface Testimonial {
    quote: string;
    name: string;
    title: string;
    avatar: string;
    episodes: any;
}


const Testimonials: React.FC = () => {
    const { content } = useContent();
    const sliderRef = useRef<HTMLDivElement>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const touchStartX = useRef<number | null>(null);
    const touchEndX = useRef<number | null>(null);

    const testimonials: Testimonial[] = content.testimonials;

    const handleNext = () => {
        if (isAnimating) return;
        setIsAnimating(true);
        setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
        setTimeout(() => setIsAnimating(false), 500);
    };

    const handlePrev = () => {
        if (isAnimating) return;
        setIsAnimating(true);
        setCurrentIndex(
            (prevIndex) => (prevIndex - 1 + testimonials.length) % testimonials.length
        );
        setTimeout(() => setIsAnimating(false), 500);
    };

    const handleTouchStart = (event: TouchEvent) => {
        var touch: any = event.touches[0];
        touchStartX.current = touch.clientX;
    };

    const handleTouchMove = (event: TouchEvent) => {
        var touch: any = event.touches[0];
        touchEndX.current = touch.clientX;
    };

    const handleTouchEnd = () => {
        if (touchStartX.current === null || touchEndX.current === null) return;

        const touchDelta = touchStartX.current - touchEndX.current;

        if (touchDelta > 50) {
            // Swipe left
            handleNext();
        } else if (touchDelta < -50) {
            // Swipe right
            handlePrev();
        }

        // Reset touch positions
        touchStartX.current = null;
        touchEndX.current = null;
    };

    useEffect(() => {
        const slider = sliderRef.current;

        if (slider) {
            slider.addEventListener("touchstart", handleTouchStart);
            slider.addEventListener("touchmove", handleTouchMove);
            slider.addEventListener("touchend", handleTouchEnd);
        }

        return () => {
            if (slider) {
                slider.removeEventListener("touchstart", handleTouchStart);
                slider.removeEventListener("touchmove", handleTouchMove);
                slider.removeEventListener("touchend", handleTouchEnd);
            }
        };
    }, []);

    useEffect(() => {
        const slider = sliderRef.current;
        // const duration = parseInt(slider?.getAttribute("data-duration") || "500");
        const autoPlay = slider?.getAttribute("data-autoplay") === "true";
        const delay = parseInt(slider?.getAttribute("data-delay") || "4000");

        let autoPlayInterval: NodeJS.Timeout;

        if (autoPlay) {
            autoPlayInterval = setInterval(() => {
                handleNext();
            }, delay);
        }

        return () => {
            if (autoPlayInterval) clearInterval(autoPlayInterval);
        };
    }, [currentIndex]);

    const calculateTransform = (index: number): string => {
        const cardWidth = sliderRef.current?.offsetWidth || 1160;
        const difference = index - currentIndex;
        
        // Transform değerini ters çeviriyoruz (negatif -> pozitif)
        const translateX = difference * cardWidth;  // Eksi işaretini kaldırdık
        
        return `translateX(${translateX}px)`;
    };


    const calculateOpacity = (index: number): number => {
        return index === currentIndex ? 1 : 0;
    };

    return (
        <section className="section-regular">
            <div className="container-regular">
                <div ref={sliderRef} data-delay="4000" data-animation="outin" className="testimonial-slider-2 w-slider" data-autoplay="false" data-easing="ease-in-out-quart" data-hide-arrows="false" data-disable-swipe="false" data-autoplay-limit="0" data-nav-spacing="3" data-duration="500" data-infinite="true">
                    <div className="w-slider-mask" style={{ position: "relative", overflow: "hidden", height: "auto" }}>
                        {testimonials.map((testimonial, index) => (
                            <div
                                className={`testimonial-slide w-slide ${index === currentIndex ? "active" : "inactive"
                                    }`}
                                key={index}
                                style={{
                                    transition: "transform 500ms ease, opacity 500ms ease",
                                    transform: calculateTransform(index),
                                    opacity: calculateOpacity(index),
                                    ...(index === currentIndex ? {} : {
                                        position: "absolute",
                                        width: "100%",
                                        left: 0,
                                        top: 0,
                                    })
                                }}>
                                <div className="testimonial-slide-wrapper">
                                    <div className="relative">
                                        <div className="testimonial-avatar">
                                            <img src={testimonial.avatar} sizes="(max-width: 479px) 240px, (max-width: 767px) 306px, (max-width: 991px) 254px, 306px" alt={`${testimonial.name} Avatar`} loading="lazy" className="image-cover" />
                                        </div>
                                        <div className="testimonial-quote">
                                            <svg
                                                width="48"
                                                height="48"
                                                viewBox="0 0 48 48"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M9.16682 34.6422C7.10632 32.455 6 30 6 26.0208C6 19.0218 10.913 12.7474 18.0612 9.64648L19.8466 12.4017C13.1761 16.0109 11.8724 20.6922 11.3513 23.6442C12.4253 23.0888 13.8312 22.8934 15.2094 23.0212C18.8182 23.3556 21.6624 26.3182 21.6624 30C21.6624 33.866 18.5283 37 14.6623 37C12.5162 37 10.4639 36.0192 9.16682 34.6422ZM29.1668 34.6422C27.1064 32.455 26 30 26 26.0208C26 19.0218 30.913 12.7474 38.0612 9.64648L39.8466 12.4017C33.176 16.0109 31.8724 20.6922 31.3512 23.6442C32.4252 23.0888 33.8312 22.8934 35.2094 23.0212C38.8182 23.3556 41.6624 26.3182 41.6624 30C41.6624 33.866 38.5284 37 34.6624 37C32.5162 37 30.464 36.0192 29.1668 34.6422Z"
                                                    fill="currentColor"
                                                />
                                            </svg>
                                        </div>
                                    </div>
                                    <div className="testimonial-content">
                                        <div className="column-large">
                                            <h6 className="paragraph-large">{testimonial.quote}</h6>
                                        </div>
                                        <div className="testimonial-detail">
                                            <div className="h5-heading">{testimonial.name}</div>
                                            <div className="caption-regular-2 text-color-gray-700">{testimonial.title}</div>
                                        </div>
                                        {testimonial.episodes && (
                                            <div className="div-block-4">
                                                {testimonial.episodes.apple && (
                                                    <a
                                                        data-wf--primary-button--variant="base"
                                                        href={testimonial.episodes.apple}
                                                        className="w-inline-block episodes-button"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        <div className="body-button-normal"></div>
                                                        <img
                                                            src="/images/episodes/apple-music.svg"
                                                            loading="lazy"
                                                            alt="Apple Podcast"
                                                            className="icon-x-large"
                                                        />
                                                    </a>
                                                )}
                                                {testimonial.episodes.spotify && (
                                                    <a
                                                        data-wf--primary-button--variant="base"
                                                        href={testimonial.episodes.spotify}
                                                        className="w-inline-block episodes-button"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        <div className="body-button-normal"></div>
                                                        <img
                                                            src="/images/episodes/podcast_spotify.svg"
                                                            loading="lazy"
                                                            alt="Spotify"
                                                            className="icon-x-large"
                                                        />
                                                    </a>
                                                )}
                                                {testimonial.episodes.youtube && (
                                                    <a
                                                        data-wf--primary-button--variant="base"
                                                        href={testimonial.episodes.youtube}
                                                        className="w-inline-block episodes-button"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        <div className="body-button-normal"></div>
                                                        <img
                                                            src="/images/episodes/podcast_youtube.svg"
                                                            loading="lazy"
                                                            alt="YouTube"
                                                            className="icon-x-large"
                                                        />
                                                    </a>
                                                )}
                                            </div>
                                        )}


                                    </div>
                                </div>
                            </div>
                        ))}
                        <div className="testimonial-slide-arrow left w-slider-arrow-left" onClick={handlePrev}>
                            <div className="icon-x-small-3 w-embed"><svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M5.21928 7.33327H13.3337V8.6666H5.21928L8.79526 12.2425L7.85246 13.1853L2.66699 7.99993L7.85246 2.81445L8.79526 3.75726L5.21928 7.33327Z" fill="currentColor"></path>
                            </svg></div>
                        </div>
                        <div className="testimonial-slide-arrow w-slider-arrow-right" onClick={handleNext}>
                            <div className="icon-x-small-3 w-embed"><svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M10.7814 7.33327L7.20539 3.75726L8.14819 2.81445L13.3337 7.99993L8.14819 13.1853L7.20539 12.2425L10.7814 8.6666H2.66699V7.33327H10.7814Z" fill="currentColor"></path>
                            </svg></div>
                        </div>
                    </div>
                    <div className="testimonial-divider"></div>
                </div>
            </div>
        </section>
    );
};

export default Testimonials;
